import React from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Typography,
  Box,
  Button,
  Icon,
  Divider,
} from "@mui/material";
import { useTranslation } from "react-i18next";

const SuccessorTrainingDialog = ({ open, onClose, data, employeeName }) => {
  const { t } = useTranslation();

  const sortedTrainings = [...(data?.trainings || [])].sort(
    (a, b) => new Date(a.endDate) - new Date(b.endDate)
  );

  const trainingCount = sortedTrainings.length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: "16px", p: 1, boxShadow: "0 20px 40px rgba(0,0,0,0.1)" },
      }}
    >
      <DialogTitle
        sx={{ m: 0, p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "10px",
              bgcolor: "#e3f2fd",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mr: 2,
            }}
          >
            <Icon sx={{ color: "#1976d2" }}>school</Icon>
          </Box>
          <Box>
            <Typography variant="h6" fontWeight="bold" sx={{ color: "#344767", lineHeight: 1.2 }}>
              {t("admin.successionPlanSteps.currStepFocus")}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {data?.stepLabel} • {employeeName}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: "text.secondary" }}>
          <Icon>close</Icon>
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {trainingCount} {t("admin.successionPlanSteps.trRemaining")}
        </Typography>

        <Divider sx={{ mb: 1 }} />

        <List disablePadding>
          {data?.trainings && data.trainings.length > 0 ? (
            data.trainings.map((tr) => (
              <ListItem key={tr.id} sx={{ px: 0, py: 1 }}>
                <ListItemIcon sx={{ minWidth: "30px" }}>
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: "#fb8c00", // Action-required Orange
                      boxShadow: "0 0 0 4px rgba(251, 140, 0, 0.1)",
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={tr.trainingName}
                  secondary={
                    tr.endDate && (
                      <Box component="span" sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                        <Icon
                          sx={{ fontSize: "0.9rem !important", mr: 0.5, color: "text.secondary" }}
                        >
                          calendar_today
                        </Icon>
                        {t("admin.strategicInfo.initiatives.endDate")}:{" "}
                        {new Date(tr.endDate).toLocaleDateString(undefined, {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </Box>
                    )
                  }
                  primaryTypographyProps={{
                    variant: "body2",
                    fontWeight: "bold",
                    color: "#344767",
                  }}
                  secondaryTypographyProps={{
                    variant: "caption",
                    color: "text.secondary",
                  }}
                />
              </ListItem>
            ))
          ) : (
            <Typography variant="body2" color="text.disabled" sx={{ textAlign: "center", py: 2 }}>
              {t("admin.successionPlanSteps.noTr")}
            </Typography>
          )}
        </List>
      </DialogContent>

      <DialogActions sx={{ p: 2, justifyContent: "center" }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            bgcolor: "#1976d2",
            color: "#fff",
            px: 4,
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: "bold",
            "&:hover": {
              bgcolor: "#1565c0",
              color: "#fff",
            },
            "& .MuiButton-label": {
              color: "#fff",
            },
          }}
        >
          {t("common.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

SuccessorTrainingDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.shape({
    stepLabel: PropTypes.string,
    trainings: PropTypes.array,
  }),
  employeeName: PropTypes.string,
};

export default SuccessorTrainingDialog;
